import "./UserMenuComponent.scss";
import {
    IComponentOptions,
    IHttpService,
    IOnDestroy,
    IOnInit,
    IRootElementService,
    IScope,
} from "angular";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {Observable, ReplaySubject} from "rxjs";
import {UserNotificationPreferencesService} from "../../../notifications/services/preferences/UserNotificationPreferencesService";
import {UserNotificationPreferencesDialogService} from "../../../notifications/services/preferences-dialog/UserNotificationPreferencesDialogService";
import {NavigationModelService} from "../../service/NavigationModelService";
import {Optional} from "../../../js/util/Optional";
import {Branding, themingEnabled, showAccountOpeningDialog, getAccountOpeningPath} from "../../../types/Branding";
import {ThemingStore} from "../../../theming/store/ThemingStore";
import {OpenTraderHelpDialogService} from "../../../skin/om/help/service/OpenTraderHelpService";
import {ThemeType} from "../../../types/UserConfig";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {LanguageService} from "../../../translation/service/LanguageService";
import {languageClass, TranslationsLanguage} from "../../../translation/config/TranslationsLanguage";
import {AccountStore} from "../../../account/store/AccountStore";

export const UserMenu = "tfUserMenu";

export function userMenu(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/user-menu/UserMenuComponent.html`,
        controller: UserMenuComponentController,
        controllerAs: "ctrl"
    };
}

export class UserMenuComponentController implements IOnInit, IOnDestroy {

    public userMenuOpen: boolean;
    public userInitial: Observable<string>;
    public isGame: Observable<boolean>;
    public isOpenTrader: Observable<boolean>;
    public imageUrl: Observable<string>;
    public showSettings: Observable<boolean>;
    public showSubscriptionMenu: Observable<boolean>;
    public showNotificationDialog: Observable<boolean>;
    public showAccountOpeningDialog: Observable<boolean>;
    public accountOpeningPath: Observable<string>;
    public readonly showThemeSelector$: Observable<boolean>;
    public readonly currentTheme$: Observable<ThemeType>;
    public readonly priceInCents$: Observable<boolean>;
    public readonly showLanguageSelect$: Observable<boolean>;
    public readonly languageSelectClass$: Observable<string>;

    private readonly togglePriceInCents$ = new ReplaySubject<boolean>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    constructor(private readonly $scope: IScope,
                private readonly $element: IRootElementService,
                private readonly accountStore: AccountStore,
                private readonly currentUserService: CurrentUserService,
                private readonly navigationModelService: NavigationModelService,
                private readonly userNotificationPreferencesService: UserNotificationPreferencesService,
                private readonly userNotificationPreferencesDialogService: UserNotificationPreferencesDialogService,
                private readonly themingStore: ThemingStore,
                private readonly openTraderHelpDialogService: OpenTraderHelpDialogService,
                private readonly languageService: LanguageService,
                private readonly $http: IHttpService) {

        this.showThemeSelector$ = this.buildShowThemeSelector();
        this.currentTheme$ = this.themingStore.currentTheme;
        this.priceInCents$ = this.buildPriceInCents();
        this.showLanguageSelect$ = this.buildShowLanguageSelect();
        this.languageSelectClass$ = this.buildLanguageSelectClass();
    }

    private buildPriceInCents(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .takeUntil(this.destroyed$)
                   .map((user) => user.preferences)
                   .map((prefs) => prefs.priceInCents)
                   .filter(isNotNull)
                   .startWith(false);
    }

    private buildShowThemeSelector(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((user) => themingEnabled(user.branding()))
                   .publishReplay(1)
                   .refCount();
    }

    private buildShowLanguageSelect(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((u) => {
                       const b = u.branding();
                       const support = u.isSupport();

                       return b === Branding.OPEN_MARKETS ||
                           b === Branding.OPEN_TRADER ||
                           (b === Branding.ALLFIN && support);
                   })
                   .publishReplay(1)
                   .refCount();
    }

    private buildLanguageSelectClass(): Observable<string> {
        return this.languageService
                   .current()
                   .distinctUntilChanged()
                   .map((language) => this.languageSelectClass(language))
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsClient(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((u) => u.isClient())
                   .publishReplay(1)
                   .refCount();
    }

    private languageSelectClass(language: TranslationsLanguage): string {
        switch (language) {
            case TranslationsLanguage.English:
                return languageClass(TranslationsLanguage.Chinese);
            case TranslationsLanguage.Chinese:
                return languageClass(TranslationsLanguage.English);
        }
    }

    public $onInit(): void {
        const currentUser$ = this.currentUserService
                                 .current
                                 .takeUntil(this.destroyed$);

        this.isGame = currentUser$.map((user) => user.isGame());
        this.isOpenTrader = currentUser$.map((user) => user.isOpenTrader());
        this.imageUrl = currentUser$.map((user) => user.imageUrl);
        this.userInitial = currentUser$.map((user) => user.shortName)
                                       .map((name) => name.charAt(0) ?? "?")
                                       .map((initial) => initial.toUpperCase());

        this.showNotificationDialog = this.userNotificationPreferencesService
                                          .preferences
                                          .takeUntil(this.destroyed$)
                                          .map((preferences) => preferences.hasNotificationsAvailable());

        this.showSettings = currentUser$.map((u) => u.branding())
            .map((b) => b !== Branding.ALLFIN)
            .publishReplay(1)
            .refCount();

        this.showAccountOpeningDialog = currentUser$.map((u) => u.branding())
            .map((b) => showAccountOpeningDialog(b))
            .publishReplay(1)
            .refCount();

        this.accountOpeningPath = currentUser$
            .map((u) => getAccountOpeningPath(u.branding(), u))
            .publishReplay(1)
            .refCount();

        this.showSubscriptionMenu = currentUser$.map((user) => {
            if (user.isGame()) {
                return false;
            } else {
                return user.isClient() && user.isStripeCustomer();
            }
        });

        this.navigationModelService
            .userMenuOpen
            .takeUntil(this.destroyed$)
            .subscribe((open) => {
                this.$scope.$applyAsync(() => {
                    this.userMenuOpen = open;
                });
            });
        this.watchOutsideClicks();

        this.togglePriceInCents$
            .takeUntil(this.destroyed$)
            .withLatestFrom(this.currentUserService.current)
            .map(([_, user]) => user)
            .map((user) => user.preferences)
            .map((prefs) => prefs.priceInCents)
            .map((priceInCents) => !priceInCents)
            .subscribe((priceInCents) => this.currentUserService.updatePreference("priceInCents", priceInCents));
    }

    public showNotificationPreferencesDialog() {
        this.userNotificationPreferencesDialogService.open();
    }

    public open() {
        this.navigationModelService.openUserMenu();
    }

    public close() {
        this.navigationModelService.closeUserMenu();
    }

    private watchOutsideClicks() {
        const handler = this.onOutsideClick.bind(this);

        this.navigationModelService
            .userMenuOpen
            .takeUntil(this.destroyed$)
            .subscribe((open) => {
                if (open) {
                    document.addEventListener("click", handler);
                } else {
                    document.removeEventListener("click", handler);
                }
            });
    }

    private onOutsideClick(event: Event) {
        const thisEl = this.$element[0];
        if (thisEl == null) {
            return;
        }
        const eventTarget = event.target as Node | null;

        if (eventTarget == null) {
            return;
        }

        if (!thisEl.contains(eventTarget)) {
            this.navigationModelService.closeUserMenu();
        }
    }

    public toggleTheme() {
        this.themingStore.toggleTheme();
    }

    public togglePriceInCents() {
        this.togglePriceInCents$.next(true);
    }

    public showOpenTraderHelp() {
        this.openTraderHelpDialogService.open();
    }

    public openAccount() {
        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .subscribe((user) => {
                window.open(getAccountOpeningPath(user.branding(), user) , "_blank");
            });
    }

    /* This is terrible, but is short term... hopefully */
    public toggleLanguage() {
        this.languageService
            .current()
            .take(1)
            .subscribe((language) => {
                switch (language) {
                    case TranslationsLanguage.English:
                        this.languageService.use(TranslationsLanguage.Chinese);
                        break;
                    case TranslationsLanguage.Chinese:
                        this.languageService.use(TranslationsLanguage.English);
                        break;
                }
            });
    }

    public async logout() {
        const response = await this.$http.get<string>("/tradefloor/api/logout");
        const location = Optional.of(response)
                                 .filter((resp) => resp.headers != null)
                                 .map((resp) => resp.headers!("location"))
                                 .orElse("/");
        window.location.assign(location);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
